import React, { useEffect } from "react";
import { createPortal } from "react-dom";

/**
 * Reusable centered modal with strong blurred backdrop.
 * Rendered through a React portal directly into `document.body` so that
 * `position: fixed` is not broken by an ancestor with a `transform` / `filter`.
 *
 * Props:
 *  - onClose: () => void  – called when backdrop is clicked or Esc pressed
 *  - children: the modal card content
 *  - maxWidth: tailwind class for the card (default "max-w-lg")
 *  - testId: data-testid for the backdrop
 */
const Modal = ({ onClose, children, maxWidth = "max-w-lg", testId = "modal" }) => {
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prev;
      document.removeEventListener("keydown", onKey);
    };
  }, [onClose]);

  return createPortal(
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-4"
      style={{
        background: "rgba(15, 23, 42, 0.55)",
        backdropFilter: "blur(8px)",
        WebkitBackdropFilter: "blur(8px)",
      }}
      onClick={onClose}
      data-testid={testId}
    >
      <div
        className={`bg-white rounded-2xl shadow-2xl w-full ${maxWidth} max-h-[90vh] overflow-y-auto`}
        onClick={(e) => e.stopPropagation()}
      >
        {children}
      </div>
    </div>,
    document.body
  );
};

export default Modal;
